import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { SET_REGISTRY } from './setRegistry';
import NavDrawer from './NavDrawer';

// Brand color tokens (mirror SetSummary.js)
const C = {
  gold: '#f0c040',
  goldDim: '#c9a227',
  cyan: '#00d4ff',
  green: '#00e87a',
  red: '#ff4444',
  ink: '#e8ecf5',
  inkSoft: '#7a9ab8',
  inkFaint: '#4a5a7a',
  uiBg: '#04060f',
  cardBg: '#0a1020',
  border: '#1a2540',
  rowBg: 'rgba(8,12,26,0.55)',
};

const RESPONSIVE_CSS = `
.admin-page {
  min-height: 100vh;
  background: ${C.uiBg};
  font-family: 'Rajdhani', sans-serif;
  color: ${C.ink};
  -webkit-font-smoothing: antialiased;
}
.admin-wrap {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 20px 80px;
}
.admin-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.admin-search {
  flex: 1;
  min-width: 200px;
  background: ${C.cardBg};
  border: 1px solid ${C.border};
  color: #c8d8f0;
  font-family: 'Share Tech Mono', monospace;
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 2px;
  outline: none;
}
.admin-select {
  background: ${C.cardBg};
  border: 1px solid ${C.border};
  color: ${C.inkSoft};
  font-family: 'Orbitron', monospace;
  font-size: 10px;
  letter-spacing: 1.5px;
  padding: 8px 12px;
  border-radius: 2px;
  cursor: pointer;
}
.admin-newbtn {
  background: ${C.cyan};
  color: ${C.uiBg};
  font-family: 'Orbitron', monospace;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 2px;
  padding: 9px 14px;
  text-decoration: none;
  border-radius: 2px;
  border: none;
  cursor: pointer;
}
.admin-newbtn:hover { opacity: 0.9; }
.admin-row, .admin-thead {
  display: grid;
  grid-template-columns: 80px 1fr 100px 80px 100px 90px;
  gap: 10px;
  padding: 14px 12px;
  border: 1px solid ${C.border};
  border-top: none;
  align-items: center;
}
.admin-thead {
  padding: 8px 12px;
  background: rgba(0,212,255,0.04);
  border-top: 1px solid ${C.border};
  border-radius: 2px 2px 0 0;
  font-family: 'Orbitron', monospace;
  font-size: 9px;
  letter-spacing: 2px;
  color: ${C.inkFaint};
}
.admin-row { background: ${C.rowBg}; }
.admin-row.empty { background: transparent; }
.admin-row.empty .admin-row-year,
.admin-row.empty .admin-row-name { color: ${C.inkFaint}; }
.admin-row.empty .admin-row-shortname { color: ${C.inkSoft}; }
.admin-pill {
  display: inline-block;
  font-family: 'Orbitron', monospace;
  font-size: 9px;
  letter-spacing: 1.5px;
  padding: 4px 8px;
  border-radius: 2px;
  text-align: center;
}
.admin-action {
  font-family: 'Orbitron', monospace;
  font-size: 10px;
  letter-spacing: 1px;
  cursor: pointer;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0;
}
.admin-action.edit, .admin-action.create { color: ${C.cyan}; }
.admin-action.del { color: ${C.red}; }
.admin-action-divider { color: #2a3550; margin: 0 6px; }
.admin-empty-msg {
  padding: 32px 20px;
  text-align: center;
  font-family: 'Share Tech Mono', monospace;
  font-size: 12px;
  color: ${C.inkFaint};
  letter-spacing: 1px;
  border: 1px dashed ${C.border};
  border-radius: 2px;
  margin-top: 8px;
}
@media (max-width: 720px) {
  .admin-row, .admin-thead {
    grid-template-columns: 60px 1fr 90px 80px;
    grid-template-areas:
      "year name status actions"
      "year sub sub sub"
      "year updated updated updated";
    row-gap: 6px;
  }
  .admin-thead { display: none; }
  .admin-row-year { grid-area: year; }
  .admin-row-name-wrap { grid-area: name; }
  .admin-row-sub { grid-area: sub; }
  .admin-row-status { grid-area: status; }
  .admin-row-views { display: none; }
  .admin-row-updated { grid-area: updated; font-size: 10px !important; }
  .admin-row-actions { grid-area: actions; }
}
`;

// --- Helpers ---

function statusFor(setId, summariesById) {
  const row = summariesById[setId];
  if (!row) return 'empty';
  return row.status; // 'published' or 'draft'
}

function pillStyle(status) {
  if (status === 'published') {
    return {
      background: 'rgba(0,232,122,0.12)',
      color: C.green,
      border: '1px solid rgba(0,232,122,0.3)',
    };
  }
  if (status === 'draft') {
    return {
      background: 'rgba(240,192,64,0.12)',
      color: C.gold,
      border: '1px solid rgba(240,192,64,0.3)',
    };
  }
  return {
    background: 'rgba(74,90,122,0.12)',
    color: C.inkFaint,
    border: '1px solid rgba(74,90,122,0.3)',
  };
}

function formatUpdated(ts) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) { return '—'; }
}

function navigateTo(href) {
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// --- Component ---

export default function AdminSetSummariesList({ user }) {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [summaries, setSummaries] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Check admin + load summaries
  useEffect(() => {
    let cancelled = false;
    async function load() {
      // 1. Check is_admin
      const { data: profile, error: pErr } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      if (cancelled) return;

      if (pErr || !profile?.is_admin) {
        // Not admin — bounce to home
        navigateTo('/');
        return;
      }

      setIsAdmin(true);

      // 2. Fetch all set_summaries rows
      const { data: rows, error: sErr } = await supabase
        .from('set_summaries')
        .select('set_id, status, updated_at')
        .order('updated_at', { ascending: false });

      if (cancelled) return;

      if (!sErr && rows) {
        setSummaries(rows);
      }
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [user.id]);

  // Build the unified list: every set in SET_REGISTRY, joined with Supabase data
  const summariesById = summaries.reduce((acc, r) => {
    acc[r.set_id] = r;
    return acc;
  }, {});

  let unified = SET_REGISTRY.map(set => {
    const status = statusFor(set.id, summariesById);
    const supaRow = summariesById[set.id];
    return {
      id: set.id,
      year: set.year,
      shortName: set.shortName,
      totalCards: set.totalCards,
      status,
      updatedAt: supaRow?.updated_at || null,
    };
  });

  // Apply filters
  if (statusFilter !== 'all') {
    unified = unified.filter(s => s.status === statusFilter);
  }
  if (search.trim()) {
    const q = search.trim().toLowerCase();
    unified = unified.filter(s =>
      s.shortName.toLowerCase().includes(q) ||
      s.id.toLowerCase().includes(q) ||
      String(s.year).includes(q)
    );
  }

  // Sort: published+draft first (most recent updated_at), then empty, both by year desc
  unified.sort((a, b) => {
    const aHas = a.status !== 'empty';
    const bHas = b.status !== 'empty';
    if (aHas !== bHas) return aHas ? -1 : 1;
    return b.year - a.year;
  });

  const handleNew = () => {
    // Step 5+ feature: real flow opens the editor with a chooser.
    // For now, show an alert.
    alert('+ NEW SET SUMMARY: editor not built yet (coming in next step). Click an EMPTY row CREATE link instead to start a draft for an existing set.');
  };

  const handleEdit = (setId) => {
    navigateTo(`/admin/set-summaries/${setId}`);
  };

  const handleCreate = (setId) => {
    navigateTo(`/admin/set-summaries/${setId}`);
  };

  const handleDelete = async (setId) => {
    if (!window.confirm(`Permanently delete the summary for ${setId}? This cannot be undone.`)) return;
    const { error } = await supabase
      .from('set_summaries')
      .delete()
      .eq('set_id', setId);
    if (error) {
      alert(`Delete failed: ${error.message}`);
      return;
    }
    setSummaries(prev => prev.filter(s => s.set_id !== setId));
  };

  // --- Render ---

  if (loading) {
    return (
      <div className="admin-page">
        <style dangerouslySetInnerHTML={{ __html: RESPONSIVE_CSS }} />
        {user && <NavDrawer user={user} />}
        <div className="admin-wrap" style={{ textAlign: 'center', paddingTop: '120px' }}>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '11px', color: C.cyan, letterSpacing: '4px' }}>
            // CHECKING ADMIN ACCESS
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) return null; // redirect already in flight

  return (
    <div className="admin-page">
      <style dangerouslySetInnerHTML={{ __html: RESPONSIVE_CSS }} />
      <NavDrawer user={user} />
      <div className="admin-wrap">

        {/* Header */}
        <div style={{ marginBottom: '24px', paddingRight: '56px' }}>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '4px', color: C.goldDim, marginBottom: '8px' }}>
            COLLECTION KEEPER · ADMIN
          </div>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '24px', fontWeight: 800, color: '#fff', letterSpacing: '1.5px', marginBottom: '6px' }}>
            SET SUMMARIES
          </div>
          <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: C.inkFaint, letterSpacing: '2px' }}>
            // MANAGE EVERY SET REFERENCE PAGE //
          </div>
        </div>

        {/* Controls row */}
        <div className="admin-controls">
          <input
            type="text"
            className="admin-search"
            placeholder="Search sets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="admin-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">ALL STATUSES</option>
            <option value="published">PUBLISHED</option>
            <option value="draft">DRAFT</option>
            <option value="empty">EMPTY</option>
          </select>
          <button type="button" className="admin-newbtn" onClick={handleNew}>
            + NEW SET SUMMARY
          </button>
        </div>

        {/* Table header */}
        <div className="admin-thead">
          <div>YEAR</div>
          <div>SET NAME</div>
          <div>STATUS</div>
          <div>VIEWS</div>
          <div>UPDATED</div>
          <div style={{ textAlign: 'right' }}>ACTIONS</div>
        </div>

        {/* Rows */}
        {unified.length === 0 ? (
          <div className="admin-empty-msg">No sets match your filters.</div>
        ) : (
          unified.map(s => {
            const isEmpty = s.status === 'empty';
            const pillLabel = s.status.toUpperCase();
            return (
              <div key={s.id} className={`admin-row ${isEmpty ? 'empty' : ''}`}>
                <div className="admin-row-year" style={{ fontFamily: "'Orbitron', monospace", fontSize: '16px', color: isEmpty ? C.inkFaint : C.gold, fontWeight: 700 }}>
                  {s.year}
                </div>
                <div className="admin-row-name-wrap">
                  <div className="admin-row-name" style={{ fontSize: '14px', color: '#fff' }}>{s.shortName}</div>
                  <div className="admin-row-shortname" style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: C.inkFaint, letterSpacing: '1px' }}>
                    {s.id}{s.totalCards ? ` · ${s.totalCards} cards` : ''}
                  </div>
                </div>
                <div className="admin-row-status">
                  <span className="admin-pill" style={pillStyle(s.status)}>{pillLabel}</span>
                </div>
                <div className="admin-row-views" style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', color: C.inkFaint }}>
                  —
                </div>
                <div className="admin-row-updated" style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: C.inkFaint }}>
                  {formatUpdated(s.updatedAt)}
                </div>
                <div className="admin-row-actions" style={{ textAlign: 'right' }}>
                  {isEmpty ? (
                    <button type="button" className="admin-action create" onClick={() => handleCreate(s.id)}>
                      CREATE
                    </button>
                  ) : (
                    <>
                      <button type="button" className="admin-action edit" onClick={() => handleEdit(s.id)}>
                        EDIT
                      </button>
                      <span className="admin-action-divider">|</span>
                      <button type="button" className="admin-action del" onClick={() => handleDelete(s.id)}>
                        DEL
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}

        {/* Footer hint */}
        <div style={{ marginTop: '24px', fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: C.inkFaint, letterSpacing: '1px' }}>
          // {unified.length} {unified.length === 1 ? 'set' : 'sets'} shown · {summaries.filter(r => r.status === 'published').length} published · {summaries.filter(r => r.status === 'draft').length} draft
        </div>

      </div>
    </div>
  );
}
